import { Breadcrumb, Select, theme } from 'antd';
import { ReactNode } from 'react';
import { getIconByName } from '../IconMapping/IconMapping';
import { useGetDataQuery } from 'common/commonRedux/getRTKApi/baseApi';
import { filterData, setFilterData } from 'common/commonRedux/feature/filteringSlice';
import { useAllClients } from 'common/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
const { useToken } = theme;
export const BreadcrumbNav = () => {
  const { t } = useTranslation();
  const isEnglish = t('dashboard:isEN');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const tokenData =
    useSelector((state: any) => state.auth?.profileData?.tokenData) || {};
  const getFilterData = useSelector(filterData);
  const { clients } = useAllClients();
  const clientOptions = clients?.map((data: any) => ({
    label: data.employeeName,
    value: data.employeeId,
  }));
  const { data: menuList = [], isLoading } = useGetDataQuery({
    params: { userId: tokenData?.userid },
    urlObjKey: 'GetMenuList',
  });

  const breadcrumbMap: Record<string, { label: string; icon: ReactNode; strTo: string }> =
    menuList?.reduce(
      (acc: any, item: any) => {
        acc[item.strTo] = {
          label: isEnglish ? item.strLabel : item.strBanglaLabel,
          icon: getIconByName(item.strIcon),
          strTo: item.strTo,
        };
        item.secondMenu?.forEach(
          (subItem: { strTo: string; strLabel: string; strBanglaLabel: string; strIcon: string }) => {
            acc[subItem.strTo] = {
              label: isEnglish ? subItem.strLabel : subItem.strBanglaLabel,
              icon: getIconByName(subItem.strIcon),
              strTo: subItem.strTo,
            };
          }
        );
        return acc;
      },
      {} as Record<string, { label: string; icon: ReactNode; strTo: string }>
    );
  // Map parent module paths to their first sub-menu strTo for navigation
  const parentNavMap: Record<string, string> = menuList?.reduce(
    (acc: any, item: any) => {
      if (item.secondMenu?.length > 0) {
        acc[item.strTo] = item.secondMenu[0].strTo;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  const { token } = useToken();
  // Ensure "/account-settings" is explicitly added to breadcrumbMap
  if (!breadcrumbMap['/account-settings']) {
    breadcrumbMap['/account-settings'] = {
      label: 'Account Settings',
      icon: getIconByName('Organization Profile'),
      strTo: '/account-settings',
    };
  }
  if (!breadcrumbMap['/settings']) {
    breadcrumbMap['/settings'] = {
      label: 'Settings',
      icon: getIconByName('Settings'),
      strTo: '/settings',
    };
  }
  if (!breadcrumbMap['/manage-account']) {
    breadcrumbMap['/manage-account'] = {
      label: 'Manage Account',
      icon: getIconByName('Manage Account'),
      strTo: '/manage-account/billing',
    };
  }
  if (!breadcrumbMap['/manage-account/billing']) {
    breadcrumbMap['/manage-account/billing'] = {
      label: 'Billing',
      icon: getIconByName('Billing'),
      strTo: '/manage-account/billing',
    };
  }
  if (!breadcrumbMap['/manage-account/payment']) {
    breadcrumbMap['/manage-account/payment'] = {
      label: 'Payment',
      icon: getIconByName('Billing'),
      strTo: '/manage-account/payment',
    };
  }
  if (!breadcrumbMap['/manage-account/payment/Success']) {
    breadcrumbMap['/manage-account/payment/Success'] = {
      label: 'Payment Success',
      icon: getIconByName('Billing'),
      strTo: '/manage-account/payment/Success',
    };
  }
  if (!breadcrumbMap['/manage-account/payment/Failed']) {
    breadcrumbMap['/manage-account/payment/Failed'] = {
      label: 'Payment Failed',
      icon: getIconByName('Billing'),
      strTo: '/manage-account/payment/Failed',
    };
  }
  if (!breadcrumbMap['/manage-account/payment/Cancelled']) {
    breadcrumbMap['/manage-account/payment/Cancelled'] = {
      label: 'Payment Cancelled',
      icon: getIconByName('Billing'),
      strTo: '/manage-account/payment/Cancelled',
    };
  }
  const homeBreadcrumb = breadcrumbMap['/'] || {
    label: 'Dashboard',
    icon: getIconByName('Dashboard'),
  };
  // Generate breadcrumb items based on pathname
  const breadcrumbItems =
    pathname === '/'
      ? [
        {
          key: '/',
          title: (
            <span style={{ color: token?.colorText }} className="font-bold">
              {homeBreadcrumb.icon} {homeBreadcrumb.label}
            </span>
          ),
        },
      ]
      : pathname
        .split('/')
        .filter(Boolean)
        .map((_, index) => {
          const pathSegments = pathname.split('/').filter(Boolean);
          const url = `/${pathname
            .split('/')
            .filter(Boolean)
            .slice(0, index + 1)
            .join('/')}`;
          const pathKey = pathSegments[index];
          const previousPath = `/${pathSegments.slice(0, index).join('/')}`;
          const isProjectDetailsRoute =
            previousPath === '/manage-project/projects' && /^\d+$/.test(pathKey || '');
          const isTaskDetailsRoute =
            previousPath === '/manage-project/task-details' && /^\d+$/.test(pathKey || '');
          const isTaskActivityMonitoringRoute =
            previousPath === '/manage-project/task-activity-chart' && /^\d+$/.test(pathKey || '');
          const isTaskActivityReportRoute =
            previousPath === '/manage-project/task-activity-report' && /^\d+$/.test(pathKey || '');
          const isTaskMonitoringRoute =
            previousPath === '/manage-project/task-monitoring' && /^\d+$/.test(pathKey || '');
          const isEditTaskIdRoute =
            previousPath === '/manage-project/edit-task' && /^\d+$/.test(pathKey || '');
          const isEditProjectIdRoute =
            previousPath === '/manage-project/edit-project' && /^\d+$/.test(pathKey || '');
          const isEmployeeDetailsRoute =
            previousPath === '/employee-management/employee-details' && !!pathKey;
          const isDeviceDetailsHostname =
            previousPath === '/employee-management/device-details' && !!pathKey;
          const isDeviceDetailsEmployeeId =
            previousPath.startsWith('/employee-management/device-details/') &&
            pathSegments[index - 1] !== 'device-details' &&
            previousPath !== '/employee-management/device-details';
          const staticRouteMap: Record<string, { label: string; iconKey: string; navTo?: string }> = {
            '/manage-project/create-task': { label: t('layout:breadcrumb.labels.createTask'), iconKey: 'Create Task', navTo: '/manage-project/tasks' },
            '/manage-project/create-project': { label: t('layout:breadcrumb.labels.createProject'), iconKey: 'Create Project', navTo: '/manage-project/projects' },
            '/manage-project/edit-task': { label: t('layout:breadcrumb.labels.editTask'), iconKey: 'Edit Task', navTo: '/manage-project/tasks' },
            '/manage-project/edit-project': { label: t('layout:breadcrumb.labels.editProject'), iconKey: 'Edit Project', navTo: '/manage-project/projects' },
            '/manage-project/task-details': { label: t('layout:breadcrumb.labels.tasks'), iconKey: 'Tasks', navTo: '/manage-project/tasks' },
            '/manage-project/task-monitoring': { label: t('layout:breadcrumb.labels.tasks'), iconKey: 'Tasks', navTo: '/manage-project/tasks' },
            '/manage-project/task-activity-chart': { label: t('layout:breadcrumb.labels.tasks'), iconKey: 'Tasks', navTo: '/manage-project/tasks' },
            '/manage-project/task-activity-report': { label: t('layout:breadcrumb.labels.tasks'), iconKey: 'Tasks', navTo: '/manage-project/tasks' },
            '/manage-project/deleted-tasks': { label: t('layout:breadcrumb.labels.deletedTasks'), iconKey: 'Tasks', navTo: '/manage-project/tasks' },
            '/employee-management/employee-details': { label: t('layout:breadcrumb.labels.employees'), iconKey: 'Employees', navTo: '/employee-management/employees' },
            '/employee-management/device-details': { label: t('layout:breadcrumb.labels.deviceList'), iconKey: 'Device Details', navTo: '/employee-management/device-list' },
          };
          const staticMatch = staticRouteMap[url];
          const dynamicBreadcrumb = isProjectDetailsRoute
            ? {
              label: t('layout:breadcrumb.labels.projectDetails'),
              icon: getIconByName('Project Details'),
            }
            : isTaskDetailsRoute
              ? {
                label: t('layout:breadcrumb.labels.taskDetails'),
                icon: getIconByName('Task Details'),
              }
              : isTaskActivityReportRoute
                ? {
                  label: t('layout:breadcrumb.labels.taskActivityAttendance'),
                  icon: getIconByName('Task Activity Attendance'),
                }
              : isTaskActivityMonitoringRoute
                ? {
                  label: t('layout:breadcrumb.labels.taskActivityChart'),
                  icon: getIconByName('Task Activity Chart'),
                }
              : isTaskMonitoringRoute
                ? {
                  label: t('layout:breadcrumb.labels.taskMonitoring'),
                  icon: getIconByName('Task Monitoring'),
                }
                : isEditTaskIdRoute
                  ? {
                    label: t('layout:breadcrumb.labels.editTask'),
                    icon: getIconByName('Edit Task'),
                  }
                  : isEditProjectIdRoute
                    ? {
                      label: t('layout:breadcrumb.labels.editProject'),
                      icon: getIconByName('Edit Project'),
                    }
                    : isEmployeeDetailsRoute
                      ? {
                        label: '__employee_switcher__',
                        icon: null,
                      }
                      : isDeviceDetailsHostname
                        ? {
                          label: pathKey || '',
                          icon: null,
                        }
                        : isDeviceDetailsEmployeeId
                          ? {
                            label: '__skip__',
                            icon: null,
                          }
                          : staticMatch
                            ? {
                              label: staticMatch.label,
                              icon: getIconByName(staticMatch.iconKey),
                            }
                            : null;
          const { label, icon } = breadcrumbMap[url] ||
            dynamicBreadcrumb || {
            label: 'Error Page',
            icon: null,
          };

          const isLastItem =
            index === pathname.split('/').filter(Boolean).length - 1;

          if (label === '__employee_switcher__') {
            const currentEmpId = pathKey;
            const currentEmpName = getFilterData?.clientName
              || clientOptions?.find((opt: any) => String(opt.value) === String(currentEmpId))?.label;
            return {
              key: url,
              title: (
                <Select
                  variant="borderless"
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ width: 200 }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder={t('layout:breadcrumb.selectEmployee')}
                  value={currentEmpName || undefined}
                  onChange={(_key, data: any) => {
                    if (!_key) {
                      dispatch(setFilterData({ clientName: undefined, clientIdString: undefined, pageNumber: 1 }));
                      return;
                    }
                    dispatch(setFilterData({ clientName: data?.label, clientIdString: data?.value, pageNumber: 1 }));
                    navigate(`/employee-management/employee-details/${data?.value}`);
                  }}
                  className="!-my-1 !font-bold"
                  suffixIcon={null}
                  options={clientOptions}
                />
              ),
            };
          }

          if (label === '__skip__') return null;

          const isFirstItem = index === 0;
          // Resolve navigation target: use navTo from staticRouteMap, strTo from menu, or parentNavMap
          const navTarget = staticMatch?.navTo || breadcrumbMap[url]?.strTo || parentNavMap[url] || url;

          return {
            key: url,
            title: isLastItem || isFirstItem ? (
              <span className={isLastItem ? 'font-bold' : ''}>
                {icon} {label}
              </span>
            ) : (
              <span
                className="cursor-pointer hover:text-[#1B73E8]"
                onClick={() => navigate(navTarget)}
              >
                {icon} {label}
              </span>
            ),
          };
        }).filter((item): item is NonNullable<typeof item> => item !== null);

  return isLoading ? (
    <p className="text-sm">{t('layout:breadcrumb.loading')}</p>
  ) : (
    <Breadcrumb
    className='!text-sm'
      style={{ color: token?.colorTextSecondary }}
      separator="|"
      items={breadcrumbItems}
    />
  );
};
